import { Injectable } from '@angular/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ReCaptchaV3Service } from 'ngx-captcha';
import { defer, from, Observable, throwError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';

import { ConfigurationService, IProviderShareableLinkAppConfig } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';

/** Shared error message thrown when reCAPTCHA can't produce a usable token, so producers and
 *  consumers block the submit instead of POSTing an empty token. */
export const RECAPTCHA_VERIFICATION_FAILED = 'recaptcha-verification-failed';

const RECAPTCHA_TIMEOUT_MS = 10000;
const RECAPTCHA_RETRY_COUNT = 1;

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  siteKey: string | null = null;
  useEnterprise: boolean = true;

  constructor(
    public configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    public recaptchaV3Service: ReCaptchaV3Service,
    public logger: Logger,
  ) {
    this.siteKey = this.configurationService.env.app?.recaptchaSiteKey || null;
  }

  executeEvent$(eventName: string): Observable<string> {
    if (!this.siteKey) {
      this.logger.warn('No site key found. Recaptcha request could not be sent.');
      return throwError(() => new Error(RECAPTCHA_VERIFICATION_FAILED));
    }

    const { siteKey } = this;
    // defer so each retry re-invokes the external call (a promise only settles once)
    return defer(() => from(this.recaptchaV3Service.executeAsPromise(
      siteKey, eventName, { useEnterprise: this.useEnterprise }
    ))).pipe(
      timeout({ first: RECAPTCHA_TIMEOUT_MS }),
      retry(RECAPTCHA_RETRY_COUNT),
      catchError((err) => {
        this.logger.warn('reCAPTCHA verification failed — blocking submit', { eventName, err });
        return throwError(() => new Error(RECAPTCHA_VERIFICATION_FAILED));
      })
    );
  }
}
